import React from 'react';
import {
  node,
  bool,
  string,
  array,
  arrayOf,
  object,
  shape,
  oneOf,
  oneOfType,
  func,
} from 'prop-types';
import cx from 'classnames';

import Label from '../Label';
import Message from '../Message';

import SelectStatic from './Select.static';

const CLASS_ROOT = 'select';

const CHOICES_EVENTS = [
  'addItem',
  'removeItem',
  'highlightItem',
  'unhighlightItem',
  'choice',
  'change',
  'search',
  'showDropdown',
  'hideDropdown',
];

export default class Select extends React.Component {
  static displayName = 'Select';

  static propTypes = {
    /** Error state. If vaule is string, it's treated as error message. */
    error: oneOfType([bool, node]),
    /** Custom error renderer. Passes select props as function parameter. */
    renderError: func,
    /** Html id attribute. */
    id: string.isRequired,
    /** Select can be cleared to default value. */
    isClearable: bool,
    /** Disabled state. */
    isDisabled: bool,
    /** Select option can be specfied via children or as options array */
    options: arrayOf(
      oneOfType([shape({ text: string, value: string }), string])
    ),
    /** Label text. */
    label: string,
    /** Custom label renderer. Passes select props as function parameter. */
    renderLabel: func,
    /** Help text. */
    help: string,
    /** Custom help renderer. Passes select props as function parameter. */
    renderHelp: func,
    /** Html name attribute. */
    name: string,
    /** Choices options. (https://github.com/jshjohnson/Choices#configuration-options) */
    choicesOptions: object,
    /** Value change callback function. */
    onChange: func,
    /** Placeholder text */
    placeholder: string,
    /** Size of element. */
    size: oneOf(['small', 'large']),
    /** Choices type. */
    type: oneOf(['text', 'one', 'multiple']),
    /** Select value. */
    value: oneOfType([string, array]),
  };

  static defaultProps = {
    onChange: (newValue, e) => {}, // eslint-disable-line
    type: 'one',
  };

  componentDidMount() {
    const { choicesOptions, value, type } = this.props;

    this.select = new SelectStatic(this.el, choicesOptions);

    if (value) {
      if (type !== 'text') {
        this.select.instance.setValueByChoice(value);
      } else {
        this.select.instance.setValue(value);
      }
    }

    CHOICES_EVENTS.forEach(eventName =>
      this.el.addEventListener(eventName, this.handleEvent)
    );
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.options) {
      this.select.instance.setChoices(
        this.props.options,
        'value',
        'label',
        true
      );
    }

    if (prevProps.value !== this.props.value) {
      this.select.instance.setValueByChoice(this.props.value);
    }
  }

  componentWillUnmount() {
    CHOICES_EVENTS.forEach(eventName =>
      this.el.removeEventListener(eventName, this.handleEvent)
    );

    this.select.destroy();
  }

  handleEvent = e => {
    const eventName = e.type.charAt(0).toUpperCase() + e.type.slice(1);
    const eventHandler = this.props[`on${eventName}`];

    if (eventHandler) {
      eventHandler(e.detail, e);
    }
  };

  render() {
    const {
      className,
      children,
      error,
      renderError,
      label,
      renderLabel,
      help,
      renderHelp,
      options,
      placeholder,
      size,
      type,
      id,
      isDisabled,
      isClearable,
      onChange, // eslint-disable-line react/prop-types
      value,
      ...other
    } = this.props;

    const classes = cx(
      CLASS_ROOT,
      {
        [`${CLASS_ROOT}--${size}`]: size,
        'is-error': error,
      },
      className
    );

    // set name from id if name is not specfied
    let { name } = other;
    name = name && id;

    const formControlClasses = cx('form-control', 'form-control--select');

    const selectLabel =
      (renderLabel && renderLabel(this.props)) ||
      (label && (
        <Label id={id} size={size} isError={!!error}>
          {label}
        </Label>
      ));

    const selectOptions =
      options &&
      // eslint-disable-next-line
      options.map(({ text, value, ...otherOptionProps }, i) => (
        <option value={value || null} {...otherOptionProps}>
          {text || options[i]}
        </option>
      ));

    const helpMessage =
      (renderHelp && renderHelp(this.props)) ||
      (help && <Message type="help">{help}</Message>);

    const errorMessage =
      (renderError && renderError(this.props)) ||
      (error &&
        typeof error === 'string' && <Message type="error">{error}</Message>);

    /*
      React will emit warning in development mode:  `Warning: Use the `defaultValue` or `value` props on <select> instead of setting `selected` on <option>``. There is no workaround for this, becoause choices.js needs `<option placeholder selected />` if placeholder is selected by default e.g. there is no value specified on select and placeholder is set.
    */

    return (
      <div className={formControlClasses}>
        {selectLabel}
        <select
          multiple={type === 'select-multiple'}
          id={id}
          name={name}
          className={classes}
          disabled={isDisabled}
          ref={el => {
            this.el = el;
          }}
          data-select=""
          data-select-clearable={isClearable}
          {...other}
        >
          {selectOptions}
          {children}
          {placeholder && (
            <option selected="selected" disabled placeholder="">
              {placeholder}
            </option>
          )}
        </select>
        {helpMessage}
        {errorMessage}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Forms/Select/Select.js